"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { BentoGrid as UiBentoGrid, BentoGridItem } from "@/components/ui/bento-grid";
import {
  Activity,
  Repeat,
  Shield,
  Bug,
  TrendingUp,
  Rocket,
  Database,
  Briefcase,
  ShoppingCart,
  Share2,
  Sparkles,
  Cpu,
  Lock,
  CheckCircle2,
  FileCode2,
  Gauge,
  KeyRound,
  Server,
  Signal,
  Smartphone,
  Terminal,
  Wifi,
} from "lucide-react";

interface StatItem {
  value: number;
  suffix: string;
  decimals: number;
  label: string;
  description: string;
}

const stats: StatItem[] = [
  { value: 99.2, suffix: "%", decimals: 1, label: "Success Rate", description: "Against Cloudflare, Akamai & DataDome" },
  { value: 8, suffix: "+", decimals: 0, label: "Live Proxies", description: "On 4G/5G Indian networks" },
  { value: 100, suffix: "GB", decimals: 0, label: "Bandwidth", description: "High-speed on Pro plan" },
  { value: 99.9, suffix: "%", decimals: 1, label: "Uptime", description: "Guaranteed availability" },
];

function AnimatedCounter({ value, suffix, decimals }: { value: number; suffix: string; decimals: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const start = performance.now();

          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(value * eased);
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toFixed(decimals)}
      <span className="text-primary">{suffix}</span>
    </span>
  );
}

function CarrierPill({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-md border border-primary/15 bg-primary/[0.07] px-2 py-1 text-[10px] font-bold tracking-wider text-primary">
      {label}
    </span>
  );
}

function DiagramShell({
  children,
  className = "",
  wide = false,
}: {
  children: React.ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div
      className={`relative w-full ${wide ? "aspect-[21/9]" : "aspect-[16/9]"} shrink-0 overflow-hidden rounded-xl border border-primary/10 bg-surface-container-lowest/80 shadow-inner shadow-primary/[0.04] ${className}`}
    >
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,103,128,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,103,128,0.08)_1px,transparent_1px)] bg-[size:28px_28px] opacity-50" />
      <div className="absolute -left-14 -top-16 size-44 rounded-full bg-primary/15 blur-3xl" />
      <div className="absolute -bottom-16 -right-10 size-44 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="relative z-10 h-full p-4">{children}</div>
    </div>
  );
}

function DiagramLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-primary/10 bg-surface-container-lowest/80 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-primary shadow-sm">
      {children}
    </span>
  );
}

function DiagramCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-primary/10 bg-surface-container-lowest/75 p-3 shadow-sm shadow-primary/[0.03] backdrop-blur-sm ${className}`}>
      {children}
    </div>
  );
}

function UseCaseMini({
  icon,
  title,
  detail,
  image,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  image: string;
}) {
  return (
    <div className="relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-primary/10 bg-surface-container-lowest/75 p-3 shadow-sm shadow-primary/[0.03]">
      <Image
        src={image}
        alt=""
        fill
        sizes="(max-width: 768px) 50vw, 18vw"
        className="object-cover opacity-85 saturate-[0.95] contrast-[1.03] transition-transform duration-700 group-hover/bento:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-surface-container-lowest/5 via-surface-container-lowest/20 to-surface-container-lowest/88" />
      <div className="absolute inset-0 bg-primary/[0.015]" />
      <span className="relative z-10 flex size-8 items-center justify-center rounded-lg border border-primary/10 bg-surface-container-lowest/80 text-primary shadow-sm backdrop-blur-sm">
        {icon}
      </span>
      <div className="relative z-10">
        <div className="text-[11px] font-bold tracking-wide text-foreground">{title}</div>
        <div className="mt-1 text-[9px] font-medium leading-snug text-muted-foreground">{detail}</div>
      </div>
    </div>
  );
}

function MapNode({ className, label }: { className: string; label: string }) {
  return (
    <div className={`absolute ${className}`}>
      <span className="absolute -inset-1 rounded-full bg-primary/20 animate-ping" />
      <span className="relative block size-2.5 rounded-full bg-primary ring-4 ring-primary/15" />
      <span className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-surface-container-lowest/85 px-1.5 py-0.5 text-[8px] font-bold text-primary shadow-sm">
        {label}
      </span>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/10 bg-primary/[0.04] px-2.5 py-1.5">
      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-[10px] font-bold text-primary">{value}</span>
    </div>
  );
}

function StatsVisual() {
  return (
    <DiagramShell wide>
      <svg className="absolute inset-0 h-full w-full text-primary/25" viewBox="0 0 700 300" aria-hidden="true">
        <path d="M108 185 C192 88 314 128 394 72 C494 2 568 78 620 138" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 8" />
        <path d="M128 196 C246 226 344 200 454 222 C536 238 590 208 642 172" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 10" />
        <path d="M190 96 L254 170 L342 116 L438 180 L530 96" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      <MapNode className="left-[18%] top-[43%]" label="DEL" />
      <MapNode className="left-[35%] top-[58%]" label="MUM" />
      <MapNode className="left-[52%] top-[38%]" label="HYD" />
      <MapNode className="left-[69%] top-[53%]" label="BLR" />
      <div className="absolute left-4 top-4">
        <DiagramLabel>Live Carrier Mesh</DiagramLabel>
      </div>
      <div className="absolute inset-x-4 bottom-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        {stats.map((stat, i) => (
          <div key={i} className="rounded-xl border border-primary/10 bg-surface-container-lowest/85 p-3 text-center shadow-sm shadow-primary/[0.04] backdrop-blur-md">
            <div className="mb-0.5 font-display text-xl font-bold tracking-tighter text-foreground md:text-2xl">
              <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals} />
            </div>
            <div className="mb-0.5 text-[10px] font-bold text-foreground">{stat.label}</div>
            <div className="text-[9px] font-medium leading-tight text-muted-foreground">{stat.description}</div>
          </div>
        ))}
      </div>
    </DiagramShell>
  );
}

function MobileFingerprintVisual() {
  return (
    <DiagramShell>
      <div className="grid h-full grid-cols-[0.85fr_1fr] items-center gap-3">
        <div className="relative mx-auto h-28 w-16 rounded-[1.35rem] border border-primary/20 bg-gradient-to-b from-primary/15 to-surface-container-lowest p-1 shadow-xl shadow-primary/10">
          <div className="h-full rounded-[1rem] border border-primary/10 bg-surface-container-lowest/80 p-2">
            <div className="mx-auto mb-3 h-1 w-5 rounded-full bg-foreground/20" />
            <Smartphone className="mx-auto mt-3 size-7 text-primary" />
            <div className="mt-4 flex items-end justify-center gap-1">
              <span className="h-2 w-1 rounded-full bg-primary/40" />
              <span className="h-3 w-1 rounded-full bg-primary/55" />
              <span className="h-4 w-1 rounded-full bg-primary/75" />
              <span className="h-5 w-1 rounded-full bg-primary" />
            </div>
          </div>
          <span className="absolute -right-3 top-7 flex size-7 items-center justify-center rounded-full border border-primary/15 bg-surface-container-lowest text-primary shadow-sm">
            <Signal className="size-3.5" />
          </span>
        </div>
        <DiagramCard>
          <div className="mb-3 flex items-center gap-2">
            <Shield className="size-4 text-primary" />
            <span className="text-[11px] font-bold text-foreground">Mobile fingerprint</span>
          </div>
          <div className="space-y-1.5">
            <StatusRow label="Carrier" value="Jio / Airtel" />
            <StatusRow label="ASN" value="Mobile ISP" />
            <StatusRow label="Source" value="Physical SIM" />
          </div>
        </DiagramCard>
      </div>
    </DiagramShell>
  );
}

function ScrapingVisual() {
  return (
    <DiagramShell>
      <div className="flex h-full items-center gap-2">
        <DiagramCard className="flex-1 p-2.5">
          <FileCode2 className="mb-2 size-4 text-primary" />
          <div className="space-y-1.5">
            <div className="h-2 rounded-full bg-foreground/15" />
            <div className="h-2 w-2/3 rounded-full bg-foreground/10" />
            <div className="h-2 w-4/5 rounded-full bg-primary/20" />
          </div>
        </DiagramCard>
        <div className="text-primary/60">→</div>
        <DiagramCard className="flex-1 p-2.5 text-center">
          <div className="mx-auto mb-2 grid size-12 grid-cols-3 gap-1 rounded-full border border-primary/15 bg-primary/[0.04] p-2">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} className="rounded-full bg-primary/60" />
            ))}
          </div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-primary">Rotating Pool</div>
        </DiagramCard>
        <div className="text-primary/60">→</div>
        <DiagramCard className="flex-1 p-2.5">
          {['price', 'stock', 'rating'].map((row) => (
            <div key={row} className="mb-1.5 flex items-center justify-between rounded-md bg-primary/[0.05] px-2 py-1 text-[9px] font-bold">
              <span className="text-muted-foreground">{row}</span>
              <CheckCircle2 className="size-3 text-primary" />
            </div>
          ))}
        </DiagramCard>
      </div>
    </DiagramShell>
  );
}

function AntiBotVisual() {
  return (
    <DiagramShell>
      <div className="grid h-full grid-cols-[1fr_0.9fr] items-center gap-3">
        <div className="space-y-2">
          {['Cloudflare', 'Akamai', 'DataDome'].map((vendor) => (
            <div key={vendor} className="flex items-center justify-between rounded-xl border border-primary/10 bg-surface-container-lowest/80 px-3 py-2 shadow-sm">
              <span className="text-[10px] font-bold text-foreground">{vendor}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider text-primary">
                <CheckCircle2 className="size-3" /> pass
              </span>
            </div>
          ))}
        </div>
        <div className="relative mx-auto flex size-24 items-center justify-center rounded-full border border-primary/15 bg-primary/[0.06]">
          <Shield className="size-10 text-primary" />
          <span className="absolute inset-3 rounded-full border border-dashed border-primary/25" />
          <span className="absolute -right-2 top-4 rounded-full bg-surface-container-lowest px-2 py-1 text-[9px] font-bold text-primary shadow-sm">99.2%</span>
        </div>
      </div>
    </DiagramShell>
  );
}

function RoutingVisual() {
  return (
    <DiagramShell>
      <div className="flex h-full flex-col justify-center gap-3">
        <div className="flex items-center gap-2">
          <DiagramCard className="min-w-20 p-2 text-center">
            <KeyRound className="mx-auto mb-1 size-4 text-primary" />
            <div className="text-[9px] font-bold text-foreground">Client Key</div>
          </DiagramCard>
          <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
          <DiagramCard className="flex-1 p-2">
            <div className="text-[10px] font-bold text-primary">ROTATING</div>
            <div className="mt-1 text-[8px] font-medium text-muted-foreground">Fresh pool IP per request</div>
          </DiagramCard>
        </div>
        <div className="flex items-center gap-2">
          <DiagramCard className="min-w-20 p-2 text-center">
            <Server className="mx-auto mb-1 size-4 text-primary" />
            <div className="text-[9px] font-bold text-foreground">Node Key</div>
          </DiagramCard>
          <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
          <DiagramCard className="flex-1 p-2">
            <div className="text-[10px] font-bold text-foreground">STICKY</div>
            <div className="mt-1 text-[8px] font-medium text-muted-foreground">Same dedicated node IP</div>
          </DiagramCard>
        </div>
      </div>
    </DiagramShell>
  );
}

function SeoVisual() {
  return (
    <DiagramShell wide>
      <div className="grid h-full grid-cols-[1.2fr_0.8fr] gap-3">
        <DiagramCard className="flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold text-foreground">SERP monitor</div>
            <DiagramLabel>India</DiagramLabel>
          </div>
          <div className="mt-3 space-y-2">
            {[
              ['mobile proxy india', '#2'],
              ['jio proxy', '#3'],
              ['socks5 india', '#5'],
            ].map(([keyword, rank]) => (
              <div key={keyword} className="flex items-center justify-between rounded-lg bg-primary/[0.05] px-3 py-2">
                <span className="text-[10px] font-semibold text-muted-foreground">{keyword}</span>
                <span className="font-display text-sm font-bold text-primary">{rank}</span>
              </div>
            ))}
          </div>
        </DiagramCard>
        <div className="grid gap-2">
          <DiagramCard className="p-3">
            <TrendingUp className="mb-2 size-4 text-primary" />
            <div className="text-[10px] font-bold text-foreground">Competitor price feed</div>
            <div className="mt-2 h-2 rounded-full bg-primary/30" />
            <div className="mt-1 h-2 w-2/3 rounded-full bg-foreground/10" />
          </DiagramCard>
          <DiagramCard className="p-3">
            <Wifi className="mb-2 size-4 text-primary" />
            <div className="text-[10px] font-bold text-foreground">Carrier-local results</div>
            <div className="mt-2 flex gap-1.5">
              <CarrierPill label="JIO" />
              <CarrierPill label="AIRTEL" />
            </div>
          </DiagramCard>
        </div>
      </div>
    </DiagramShell>
  );
}

function DeveloperVisual() {
  return (
    <DiagramShell>
      <div className="flex h-full items-center">
        <div className="w-full overflow-hidden rounded-xl border border-white/10 bg-[#101418] shadow-2xl shadow-black/20">
          <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
            <span className="size-2 rounded-full bg-red-400" />
            <span className="size-2 rounded-full bg-amber-300" />
            <span className="size-2 rounded-full bg-emerald-400" />
            <span className="ml-auto flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-zinc-500">
              <Terminal className="size-3" /> socks5
            </span>
          </div>
          <div className="space-y-1 p-3 font-mono text-[9px] leading-relaxed text-emerald-300">
            <div className="text-zinc-500"># curl through Snowpad</div>
            <div>curl --socks5 user:pass@gw.snowpad.io:9999 \</div>
            <div className="text-cyan-300">https://api.amazon.in/dp/B08L5T31M6</div>
          </div>
        </div>
      </div>
    </DiagramShell>
  );
}

function UseCasesVisual() {
  return (
    <DiagramShell wide>
      <div className="grid h-full grid-cols-2 gap-2 sm:grid-cols-4">
        <UseCaseMini
          icon={<Database className="size-4" />}
          title="Data Collection"
          detail="Products, prices, reviews"
          image="/bento/web-scraping-code.jpg"
        />
        <UseCaseMini
          icon={<Briefcase className="size-4" />}
          title="Business Intel"
          detail="Local market signals"
          image="/bento/analytics.jpg"
        />
        <UseCaseMini
          icon={<ShoppingCart className="size-4" />}
          title="E-commerce"
          detail="Catalog and stock checks"
          image="/bento/data-dashboard.jpg"
        />
        <UseCaseMini
          icon={<Share2 className="size-4" />}
          title="Social Media"
          detail="Regional verification"
          image="/bento/india-mobile.jpg"
        />
      </div>
    </DiagramShell>
  );
}

function EnterpriseVisual() {
  return (
    <DiagramShell>
      <div className="grid h-full grid-cols-2 gap-2">
        <DiagramCard className="flex flex-col items-start justify-between p-3">
          <Sparkles className="size-4 text-primary" />
          <div className="text-[10px] font-bold text-foreground">Priority Support</div>
        </DiagramCard>
        <DiagramCard className="flex flex-col items-start justify-between p-3">
          <Gauge className="size-4 text-primary" />
          <div className="text-[10px] font-bold text-foreground">SLA Paths</div>
        </DiagramCard>
        <DiagramCard className="flex flex-col items-start justify-between p-3">
          <Server className="size-4 text-primary" />
          <div className="text-[10px] font-bold text-foreground">Private Pool</div>
        </DiagramCard>
        <DiagramCard className="flex flex-col items-start justify-between p-3">
          <KeyRound className="size-4 text-primary" />
          <div className="text-[10px] font-bold text-foreground">Team Keys</div>
        </DiagramCard>
      </div>
    </DiagramShell>
  );
}

function LatencyVisual() {
  return (
    <DiagramShell>
      <svg className="absolute inset-0 h-full w-full text-primary/25" viewBox="0 0 360 200" aria-hidden="true">
        <path d="M48 130 C116 76 170 152 226 94 C264 54 304 76 328 52" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 8" />
      </svg>
      <div className="absolute left-[12%] top-[56%] rounded-xl border border-primary/10 bg-surface-container-lowest/85 px-3 py-2 shadow-sm">
        <div className="text-[10px] font-bold text-foreground">Mumbai</div>
        <div className="text-[9px] font-bold text-primary">38ms</div>
      </div>
      <div className="absolute left-[42%] top-[31%] rounded-xl border border-primary/10 bg-surface-container-lowest/85 px-3 py-2 shadow-sm">
        <div className="text-[10px] font-bold text-foreground">Delhi</div>
        <div className="text-[9px] font-bold text-primary">52ms</div>
      </div>
      <div className="absolute right-[10%] top-[48%] rounded-xl border border-primary/10 bg-surface-container-lowest/85 px-3 py-2 shadow-sm">
        <div className="text-[10px] font-bold text-foreground">BLR</div>
        <div className="text-[9px] font-bold text-primary">41ms</div>
      </div>
    </DiagramShell>
  );
}

function SecurityVisual() {
  return (
    <DiagramShell>
      <div className="grid h-full grid-cols-[0.8fr_1.2fr] items-center gap-3">
        <div className="relative mx-auto flex size-24 items-center justify-center rounded-full border border-primary/15 bg-primary/[0.07]">
          <Lock className="size-9 text-primary" />
          <span className="absolute inset-3 rounded-full border border-dashed border-primary/25" />
        </div>
        <div className="space-y-2">
          <StatusRow label="SOCKS5" value="Auth" />
          <StatusRow label="Node Keys" value="Scoped" />
          <StatusRow label="Audit Logs" value="Structured" />
        </div>
      </div>
    </DiagramShell>
  );
}

function CoverageVisual() {
  return (
    <DiagramShell>
      <svg className="absolute inset-0 h-full w-full text-primary/20" viewBox="0 0 360 200" aria-hidden="true">
        <path d="M184 24 C218 46 236 78 230 112 C224 150 196 178 166 168 C138 158 116 124 124 86 C132 50 152 28 184 24Z" fill="currentColor" />
      </svg>
      <MapNode className="left-[45%] top-[24%]" label="NCR" />
      <MapNode className="left-[38%] top-[55%]" label="MUM" />
      <MapNode className="left-[51%] top-[64%]" label="BLR" />
      <MapNode className="left-[56%] top-[45%]" label="HYD" />
      <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-1.5">
        <CarrierPill label="JIO" />
        <CarrierPill label="AIRTEL" />
        <CarrierPill label="VI" />
      </div>
    </DiagramShell>
  );
}

export function HomeBentoGrid() {
  return (
    <section id="features" className="relative py-24 sm:py-32 overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-surface via-primary/[0.005] to-surface pointer-events-none" />
      <div className="absolute top-1/3 left-1/4 w-[600px] h-[400px] bg-primary/[0.015] blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="mb-16 max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Why Snowpad</span>
          </div>
          <h2 className="text-4xl font-bold font-display text-foreground mb-4 tracking-tight">
            Built for Real-World Indian Proxies
          </h2>
          <p className="text-muted-foreground font-medium leading-relaxed max-w-xl">
            Carrier-grade mobile IPs from physical devices across India. Power scraping, anti-bot bypass, SEO
            monitoring, and ad verification without getting blocked.
          </p>
        </div>

        <UiBentoGrid className="max-w-7xl mx-auto">
          {/* Row 1: Stats */}
          <BentoGridItem
            title="99.2% Anti-Bot Success Rate"
            description="Consistently high success rates and reliable availability across Jio & Airtel mobile endpoints."
            className="md:col-span-2"
            icon={<Activity className="size-5 text-primary" />}
            header={<StatsVisual />}
          />

          {/* Row 1: Real Mobile Fingerprints */}
          <BentoGridItem
            title="Real Mobile Fingerprints"
            description="Every request originates from a physical device on a real Indian SIM. Your traffic carries genuine carrier signatures."
            className="md:col-span-1"
            icon={<Shield className="size-5 text-primary" />}
            header={<MobileFingerprintVisual />}
            footer={
              <div className="flex flex-wrap gap-2 pt-2">
                <CarrierPill label="JIO" />
                <CarrierPill label="AIRTEL" />
                <CarrierPill label="VI" />
                <CarrierPill label="BSNL" />
              </div>
            }
          />

          {/* Row 2: Web Scraping at Scale */}
          <BentoGridItem
            title="Web Scraping at Scale"
            description="Extract product data, pricing, and reviews from Indian platforms without blockades. Auto-rotate IPs or maintain sticky sessions."
            className="md:col-span-1"
            icon={<Database className="size-5 text-primary" />}
            header={<ScrapingVisual />}
          />

          {/* Row 2: Anti-Bot System Bypass */}
          <BentoGridItem
            title="Anti-Bot System Bypass"
            description="Real mobile IPs bypass Cloudflare, Akamai, DataDome, and PerimeterX without triggering rate limits."
            className="md:col-span-1"
            icon={<Bug className="size-5 text-primary" />}
            header={<AntiBotVisual />}
          />

          {/* Row 2: Flexible Pool Routing */}
          <BentoGridItem
            title="Flexible Pool Routing"
            description="Choose between auto-rotating IP pools or assign dedicated sticky SOCKS5 proxies per client key."
            className="md:col-span-1"
            icon={<Repeat className="size-5 text-primary" />}
            header={<RoutingVisual />}
          />

          {/* Row 3: Market Research & SEO */}
          <BentoGridItem
            title="Market Research & SEO"
            description="Monitor competitor pricing, track SERP rankings, and gather localized search metrics across Indian carriers."
            className="md:col-span-2"
            icon={<TrendingUp className="size-5 text-primary" />}
            header={<SeoVisual />}
          />

          {/* Row 3: Instant Developer Setup */}
          <BentoGridItem
            title="Instant Developer Setup"
            description="One SOCKS5 endpoint. Drop the credentials into Puppeteer, Playwright, Scrapy, Selenium, or cURL."
            className="md:col-span-1"
            icon={<Rocket className="size-5 text-primary" />}
            header={<DeveloperVisual />}
          />

          {/* Row 4: Use Cases */}
          <BentoGridItem
            title="Built for Every Use Case"
            description="One proxy network for data collection, business intelligence, e-commerce, and social media operations."
            className="md:col-span-2"
            icon={<Briefcase className="size-5 text-primary" />}
            header={<UseCasesVisual />}
            footer={
              <div className="pt-2 text-[10px] text-muted-foreground font-medium">
                Explore each use case in detail on our{" "}
                <a href="/use-cases" className="text-primary font-bold hover:underline">
                  Use Cases page
                </a>
                .
              </div>
            }
          />

          <BentoGridItem
            title="Enterprise Ready"
            description="Dedicated support, private pools, team keys, and custom monthly plans for production proxy workflows."
            className="md:col-span-1"
            icon={<Sparkles className="size-5 text-primary" />}
            header={<EnterpriseVisual />}
          />

          <BentoGridItem
            title="Low Latency Infrastructure"
            description="Health-checked Indian mobile endpoints with nearby routing paths for stable SOCKS5 sessions."
            className="md:col-span-1"
            icon={<Cpu className="size-5 text-primary" />}
            header={<LatencyVisual />}
          />

          <BentoGridItem
            title="Secure & Compliant"
            description="Credentialed SOCKS5 access, scoped dedicated-node keys, structured logs, and DPDP-aware handling."
            className="md:col-span-1"
            icon={<Lock className="size-5 text-primary" />}
            header={<SecurityVisual />}
          />

          <BentoGridItem
            title="Global Indian Coverage"
            description="Carrier-visible Indian mobile routes across the current pool, with coverage expansion guided by demand."
            className="md:col-span-1"
            icon={<TrendingUp className="size-5 text-primary" />}
            header={<CoverageVisual />}
          />
        </UiBentoGrid>
      </div>
    </section>
  );
}
